import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AgencyInfo {
  name: string;
  address: string;
  phone: string;
  email?: string;
  registration_number?: string;
}

interface ReservationAttestationPDFProps {
  reservationId: number;
  guestName: string;
  guestPhone?: string;
  startDate: string;
  endDate: string;
  pricePerNight: number;
  amountPaid: number;
  notes: string;
  agencyInfo: AgencyInfo;
}

export const ReservationAttestationPDF: React.FC<ReservationAttestationPDFProps> = ({
  reservationId,
  guestName,
  guestPhone,
  startDate,
  endDate,
  pricePerNight,
  amountPaid,
  notes,
  agencyInfo,
}) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#FFFFFF',
      fontFamily: 'Helvetica',
      padding: 20,
      height: '100%',
    },
    header: {
      marginBottom: 12,
      borderBottomWidth: 2,
      borderBottomColor: '#0F766E',
      paddingBottom: 8,
    },
    headerTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#0F766E',
      marginBottom: 1,
      textAlign: 'center',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    headerSubtitle: {
      fontSize: 9,
      color: '#555555',
      textAlign: 'center',
      marginBottom: 3,
    },
    agencyInfo: {
      fontSize: 9,
      color: '#666666',
      textAlign: 'center',
      lineHeight: 1.3,
    },
    attestationNumber: {
      fontSize: 8,
      color: '#999999',
      textAlign: 'center',
      marginTop: 6,
      marginBottom: 10,
    },
    section: {
      marginBottom: 10,
    },
    sectionTitle: {
      fontSize: 9,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 5,
      paddingBottom: 3,
      borderBottomWidth: 1,
      borderBottomColor: '#0F766E',
    },
    introText: {
      fontSize: 9,
      color: '#333333',
      marginBottom: 8,
      lineHeight: 1.4,
    },
    infoRow: {
      flexDirection: 'row',
      marginBottom: 4,
      fontSize: 9,
    },
    label: {
      width: '35%',
      fontWeight: 'bold',
      color: '#333333',
      fontSize: 9,
    },
    value: {
      width: '65%',
      color: '#555555',
      fontSize: 9,
      lineHeight: 1.3,
    },
    priceTable: {
      marginTop: 8,
      marginBottom: 8,
    },
    priceRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#E5E7EB',
      paddingTop: 4,
      paddingBottom: 4,
    },
    priceRowHeader: {
      backgroundColor: '#F3F4F6',
      borderBottomWidth: 2,
      borderBottomColor: '#0F766E',
      paddingTop: 5,
      paddingBottom: 5,
    },
    priceLabel: {
      width: '60%',
      fontSize: 9,
      fontWeight: 'bold',
      color: '#333333',
    },
    priceValue: {
      width: '40%',
      fontSize: 9,
      fontWeight: 'bold',
      color: '#333333',
      textAlign: 'right',
      paddingRight: 8,
    },
    totalRow: {
      fontWeight: 'bold',
      backgroundColor: '#F0F9F8',
    },
    closingText: {
      fontSize: 8,
      color: '#333333',
      marginTop: 8,
      marginBottom: 8,
      lineHeight: 1.3,
      fontStyle: 'italic',
    },
    signatureSection: {
      marginTop: 10,
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    signatureBox: {
      width: '45%',
    },
    signatureTitle: {
      fontSize: 8,
      fontWeight: 'bold',
      color: '#333333',
      marginBottom: 15,
    },
    signatureLabel: {
      fontSize: 8,
      color: '#666666',
      marginTop: 10,
    },
    footer: {
      marginTop: 8,
      paddingTop: 6,
      borderTopWidth: 1,
      borderTopColor: '#E5E7EB',
      fontSize: 7,
      color: '#999999',
      textAlign: 'center',
      lineHeight: 1.3,
    },
  });

  const parseNotes = () => {
    const lines = notes.split('\n');
    return {
      address: lines[0]?.trim() || '',
      type: lines[1]?.trim() || '',
      capacity: lines[2]?.trim() || '',
    };
  };

  const noteData = parseNotes();

  const startDateObj = new Date(startDate);
  const endDateObj = new Date(endDate);
  const nights = Math.ceil((endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60 * 60 * 24));
  const totalAmount = nights * pricePerNight;
  const balanceDue = totalAmount - amountPaid;
  const attestationNumber = String(reservationId).padStart(4, '0');
  const currentDate = new Date();

  const formatPrice = (price: number) => {
    return Math.floor(price).toLocaleString('fr-FR').replace(/\s/g, ' ') + ' DA';
  };

  const formatDateFR = (date: Date) => {
    return format(date, 'dd/MM/yyyy', { locale: fr });
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* En-tête */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{agencyInfo.name}</Text>
          <Text style={styles.headerSubtitle}>AGENCE IMMOBILIÈRE ET ADMINISTRATION DE BIENS IMMOBILIERS</Text>
          <View style={styles.agencyInfo}>
            <Text>{agencyInfo.address} - Tél: {agencyInfo.phone}</Text>
          </View>
        </View>

        {/* Numéro et date d'attestation */}
        <View style={styles.attestationNumber}>
          <Text>Numéro de l'attestation: {attestationNumber}</Text>
          <Text>Date: {formatDateFR(currentDate)}</Text>
        </View>

        {/* Titre principal */}
        <Text style={styles.sectionTitle}>ATTESTATION DE RÉSERVATION – LOCATION COURTE DURÉE</Text>

        {/* Texte d'introduction */}
        <Text style={styles.introText}>
          Nous, soussignés, {agencyInfo.name}, attestons que :
        </Text>

        {/* Section Locataire */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informations du locataire</Text>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Nom et prénom :</Text>
            <Text style={styles.value}>{guestName.toUpperCase()}</Text>
          </View>
          {guestPhone && (
            <View style={styles.infoRow}>
              <Text style={styles.label}>Téléphone :</Text>
              <Text style={styles.value}>{guestPhone}</Text>
            </View>
          )}
        </View>

        {/* Section Bien */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informations du bien immobilier loué</Text>
          {notes && (
            <Text style={styles.value}>{notes}</Text>
          )}
        </View>

        {/* Section Période */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Période de location</Text>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Date d'entrée :</Text>
            <Text style={styles.value}>{formatDateFR(startDateObj)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Date de sortie :</Text>
            <Text style={styles.value}>{formatDateFR(endDateObj)} avant 12h</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Nombre de nuitées :</Text>
            <Text style={styles.value}>{nights}</Text>
          </View>
        </View>

        {/* Section Tarifs */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Détails financiers</Text>
          <View style={styles.priceTable}>
            <View style={[styles.priceRow, styles.priceRowHeader]}>
              <Text style={styles.priceLabel}>Montant total de la location</Text>
              <Text style={styles.priceValue}>{formatPrice(totalAmount)}</Text>
            </View>
            <View style={styles.priceRow}>
              <Text style={styles.priceLabel}>Prix par nuitée</Text>
              <Text style={styles.priceValue}>{formatPrice(pricePerNight)}</Text>
            </View>
            <View style={styles.priceRow}>
              <Text style={styles.priceLabel}>Acompte versé</Text>
              <Text style={styles.priceValue}>{formatPrice(amountPaid)}</Text>
            </View>
            <View style={[styles.priceRow, styles.totalRow]}>
              <Text style={styles.priceLabel}>Solde restant</Text>
              <Text style={styles.priceValue}>{formatPrice(balanceDue)}</Text>
            </View>
          </View>
        </View>

        {/* Texte de fermeture */}
        <Text style={styles.closingText}>
          Cette attestation est délivrée en guise de confirmation de la réservation. Elle ne constitue pas un contrat de location définitif.
        </Text>

        {/* Section Signature */}
        <View style={styles.signatureSection}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureTitle}>Signature et cachet de l'agence :</Text>
            <Text style={styles.signatureLabel}>{agencyInfo.name}</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureTitle}>Fait à, le {formatDateFR(currentDate)}</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Document généré automatiquement via le logiciel REDIMMO</Text>
        </View>
      </Page>
    </Document>
  );
};
